import { ArrowDown, ArrowUp, ImageOff, Plus, Trash2 } from "lucide-react";
import { type ChangeEvent, type DragEvent, useRef, useState } from "react";
import { toast } from "sonner";
import { type PhotoItem, useDeletePhoto, usePhotos, useUpdatePhoto, useUploadPhoto } from "../../../hooks/use-files";
import { useMoveImage } from "../../../hooks/use-imagery";
import { ApiError } from "../../../lib/api";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { useConfirm } from "../../ui/confirm-dialog";
import { ErrorState, LoadingSpinner } from "../../ui/loading";
import { Section } from "../view/section";
import { getImageDragRef, setImageDragRef } from "./imagery/drag-payload";

const MAX_PHOTOS = 8;

interface Props {
  recordId: string;
}

function PhotoCard({
  photo,
  isFirst,
  isLast,
  isDragOver,
  onMoveUp,
  onMoveDown,
  onDelete,
  onSaveCaption,
  onDragStart,
  onDragOverCard,
  onDragLeaveCard,
  onDropCard,
}: {
  photo: PhotoItem;
  isFirst: boolean;
  isLast: boolean;
  isDragOver: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onSaveCaption: (caption: string) => void;
  onDragStart: (e: DragEvent<HTMLDivElement>) => void;
  onDragOverCard: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeaveCard: () => void;
  onDropCard: (e: DragEvent<HTMLDivElement>) => void;
}): React.ReactElement {
  const [caption, setCaption] = useState(photo.caption ?? "");

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop reorder target, controls below cover keyboard use
    <div
      className={cn("rounded-md border overflow-hidden", isDragOver && "ring-2 ring-primary")}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOverCard}
      onDragLeave={onDragLeaveCard}
      onDrop={onDropCard}
      data-testid={`photo-card-${photo.id}`}
    >
      <div className="aspect-square bg-muted">
        <img src={photo.url} alt={photo.caption ?? "Photo"} className="h-full w-full object-cover" />
      </div>
      <div className="p-2 space-y-2">
        <input
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onBlur={() => {
            if (caption !== (photo.caption ?? "")) {
              onSaveCaption(caption);
            }
          }}
          placeholder="Caption"
          className="w-full rounded-md border border-input bg-white px-2 py-1 text-xs"
          data-testid={`photo-caption-${photo.id}`}
        />
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={isFirst}
              className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              data-testid={`photo-move-up-${photo.id}`}
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={isLast}
              className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              data-testid={`photo-move-down-${photo.id}`}
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </div>
          <button
            type="button"
            onClick={onDelete}
            className="text-muted-foreground hover:text-destructive"
            data-testid={`photo-delete-${photo.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function SectionPhotosForm({ recordId }: Props): React.ReactElement {
  const { data, isLoading, isError, error } = usePhotos(recordId);
  const uploadPhoto = useUploadPhoto(recordId);
  const updatePhoto = useUpdatePhoto(recordId);
  const deletePhoto = useDeletePhoto(recordId);
  const moveImage = useMoveImage(recordId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const { confirm, dialog } = useConfirm();

  const photos = [...(data?.photos ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const atLimit = photos.length >= MAX_PHOTOS;

  const handleFileSelected = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    uploadPhoto.mutate(
      { file },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.code === "PHOTO_LIMIT") {
            toast.error("Maximum 8 photos allowed");
          } else {
            toast.error(err instanceof Error ? err.message : "Failed to upload photo");
          }
        },
      },
    );
  };

  const swapSortOrder = (a: PhotoItem, b: PhotoItem): void => {
    updatePhoto.mutate({ photoId: a.id, sortOrder: b.sortOrder });
    updatePhoto.mutate({ photoId: b.id, sortOrder: a.sortOrder });
  };

  const handleDelete = async (photoId: string): Promise<void> => {
    const ok = await confirm({
      title: "Delete photo",
      description: "This removes the photo from the record.",
      destructive: true,
    });
    if (!ok) return;
    deletePhoto.mutate(photoId, {
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : "Failed to delete photo");
      },
    });
  };

  const handleDropOnGrid = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    const ref = getImageDragRef(e);
    if (!ref || "photoId" in ref) return;

    moveImage.mutate(
      { from: ref, to: { target: "photos" } },
      {
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : "Failed to move image");
        },
      },
    );
  };

  const handleDropOnCard =
    (target: PhotoItem) =>
    (e: DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setDragOverId(null);
      const ref = getImageDragRef(e);
      if (!ref) return;

      if ("photoId" in ref) {
        const source = photos.find((p) => p.id === ref.photoId);
        if (source && source.id !== target.id) swapSortOrder(source, target);
        return;
      }

      moveImage.mutate(
        { from: ref, to: { target: "photos" } },
        {
          onError: (err) => {
            toast.error(err instanceof Error ? err.message : "Failed to move image");
          },
        },
      );
    };

  return (
    <Section id="photos" title="Photos">
      {isLoading && (
        <div className="flex justify-center py-6">
          <LoadingSpinner />
        </div>
      )}
      {isError && <ErrorState message={error instanceof Error ? error.message : "Failed to load photos"} />}
      {!isLoading && !isError && (
        <div className="space-y-3">
          {photos.length === 0 ? (
            // biome-ignore lint/a11y/noStaticElementInteractions: drop target for imagery slot images
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDropOnGrid}
              className="flex flex-col items-center gap-2 py-6 text-sm text-muted-foreground border-2 border-dashed rounded-md"
              data-testid="photos-empty-drop-zone"
            >
              <ImageOff className="h-6 w-6" />
              No photos yet.
            </div>
          ) : (
            // biome-ignore lint/a11y/noStaticElementInteractions: drop target for imagery slot images
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDropOnGrid}
              className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
              data-testid="photos-grid"
            >
              {photos.map((photo, index) => (
                <PhotoCard
                  key={photo.id}
                  photo={photo}
                  isFirst={index === 0}
                  isLast={index === photos.length - 1}
                  isDragOver={dragOverId === photo.id}
                  onMoveUp={() => {
                    const prev = photos[index - 1];
                    if (prev) swapSortOrder(photo, prev);
                  }}
                  onMoveDown={() => {
                    const next = photos[index + 1];
                    if (next) swapSortOrder(photo, next);
                  }}
                  onDelete={() => handleDelete(photo.id)}
                  onSaveCaption={(caption) => updatePhoto.mutate({ photoId: photo.id, caption })}
                  onDragStart={(e) => setImageDragRef(e, { photoId: photo.id })}
                  onDragOverCard={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverId(photo.id);
                  }}
                  onDragLeaveCard={() => setDragOverId(null)}
                  onDropCard={(e) => {
                    e.stopPropagation();
                    handleDropOnCard(photo)(e);
                  }}
                />
              ))}
            </div>
          )}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelected}
              className="hidden"
              data-testid="photo-file-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={atLimit || uploadPhoto.isPending}
              onClick={() => fileInputRef.current?.click()}
              data-testid="add-photo"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Photo
            </Button>
            {atLimit && <p className="text-xs text-muted-foreground mt-1">Maximum 8 photos</p>}
          </div>
        </div>
      )}
      {dialog}
    </Section>
  );
}
